import React, { useEffect, useState } from "react";
import { enqueueSnackbar } from "notistack";
import * as Yup from "yup";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../ui/card";
import { InputText } from "../../InputText";
import { Label } from "../../ui/label";
import { Button } from "../../ui/button";
import { Badge } from "../../ui/badge";
import { UserCheck, Loader2, X, ChevronRight, ChevronLeft, Shield, Check, Search, Award } from "lucide-react";
import { api } from "../../../services/api";

interface Permissao {
  id: string;
  permissao: string;
  categoria?: string;
}

interface Patente {
  id: number | string; // Pode ser number ou UUID string
  patente: string;
  abreviacao: string;
  ordem: number;
  categoria: string;
  status?: string;
}

interface Perfil {
  id: string;
  perfil: string;
  criado_em: string;
  alterado_em: string;
  descricao?: string;
  permissoes?: string[];
  patentes_ids?: (number | string)[]; // Pode ser number ou UUID string
  status?: string;
}

interface FormValues {
  perfil: string;
  permissoes: string[];
  patentes_ids: (number | string)[]; // Pode ser number ou UUID string
}

interface PerfilModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialData?: Perfil | null;
  onSave?: () => void;
  editingPerfil?: Perfil | null; // Manter para compatibilidade
  onSuccess?: () => void; // Manter para compatibilidade
}

const validationSchema = Yup.object({
  perfil: Yup.string()
    .required("Nome do perfil é obrigatório")
    .min(2, "Nome deve ter pelo menos 2 caracteres")
    .max(100, "Nome deve ter no máximo 100 caracteres"),
});

export function PerfilModal({
  isOpen,
  onClose,
  initialData = null,
  onSave,
  editingPerfil = null,
  onSuccess
}: PerfilModalProps) {
  // Usar initialData se fornecido, senão usar editingPerfil para compatibilidade
  const perfilData = initialData || editingPerfil;
  const [loading, setLoading] = useState(false);
  const [loadingPermissoes, setLoadingPermissoes] = useState(false);
  const [todasPermissoes, setTodasPermissoes] = useState<Permissao[]>([]);
  const [todasPatentes, setTodasPatentes] = useState<Patente[]>([]);
  const [selectedPatenteIds, setSelectedPatenteIds] = useState<Set<string>>(new Set());
  const [formData, setFormData] = useState<FormValues>({
    perfil: "",
    permissoes: [],
    patentes_ids: [] as (number | string)[],
  });
  const [errors, setErrors] = useState<Partial<FormValues>>({});
  const [filterText, setFilterText] = useState('');

  useEffect(() => {
    if (isOpen) {
      carregarPermissoes();
      carregarPatentes();
    }

    if (perfilData) {
      setFormData({
        perfil: perfilData.perfil,
        permissoes: perfilData.permissoes || [],
        patentes_ids: [],
      });
      setSelectedPatenteIds(new Set((perfilData.patentes_ids || []).map(String)));
    } else {
      setFormData({
        perfil: "",
        permissoes: [],
        patentes_ids: [],
      });
      setSelectedPatenteIds(new Set());
    }
    setErrors({});
  }, [perfilData, isOpen]);

  const carregarPermissoes = async () => {
    try {
      setLoadingPermissoes(true);
      const response = await api.get("/permissoes");
      // Ordenar permissões por categoria e depois por nome da permissão
      const permissoesOrdenadas = response.data.sort((a: Permissao, b: Permissao) => {
        const catA = a.categoria || "Sem Categoria";
        const catB = b.categoria || "Sem Categoria";
        if (catA !== catB) {
          return catA.localeCompare(catB);
        }
        return a.permissao.localeCompare(b.permissao);
      });
      setTodasPermissoes(permissoesOrdenadas);
    } catch (error) {
      console.error("Erro ao carregar permissões:", error);
      // Se a API não existir, usar dados mock
      setTodasPermissoes([
        { id: "1", permissao: "alterar_nick", categoria: "Militares" },
        { id: "2", permissao: "alterar_sigla_militar", categoria: "Militares" },
        { id: "3", permissao: "criar_alterar_militar", categoria: "Militares" },
        { id: "4", permissao: "criar_alterar_painel_oficial", categoria: "Painéis (legado)" },
        { id: "5", permissao: "criar_alterar_painel_praca", categoria: "Painéis (legado)" },
        { id: "6", permissao: "criar_alterar_treinamento", categoria: "Painéis (legado)" },
        { id: "7", permissao: "criar_sigla", categoria: "Siglas" },
      ]);
    } finally {
      setLoadingPermissoes(false);
    }
  };

  const carregarPatentes = async () => {
    try {
      const response = await api.get("/patentes");
      const patentesAtivas = response.data
        .filter((p: Patente) => !p.status || p.status === "A")
        .sort((a: Patente, b: Patente) => a.ordem - b.ordem);
      setTodasPatentes(patentesAtivas);
    } catch (error) {
      console.error("Erro ao carregar patentes:", error);
    }
  };

  const validateForm = () => {
    try {
      validationSchema.validateSync(formData, { abortEarly: false });
      setErrors({});
      return true;
    } catch (error: any) {
      const validationErrors: Partial<FormValues> = {};
      error.inner.forEach((err: any) => {
        validationErrors[err.path as keyof FormValues] = err.message;
      });
      setErrors(validationErrors);
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      setLoading(true);

      // Converter os IDs selecionados para o formato correto
      const patentesIdsArray = Array.from(selectedPatenteIds).map(strId => {
        // Tenta converter para número, se falhar mantém como string
        const numId = Number(strId);
        return Number.isFinite(numId) && numId > 0 ? numId : strId;
      });

      const payload = {
        ...formData,
        patentes_ids: patentesIdsArray,
      };

      if (perfilData) {
        await api.put(`/perfis/${perfilData.id}`, payload);
        enqueueSnackbar("Perfil atualizado com sucesso!", { variant: "success" });
      } else {
        await api.post("/perfis", payload);
        enqueueSnackbar("Perfil criado com sucesso!", { variant: "success" });
      }

      // Chamar callback de sucesso
      if (onSave) {
        onSave();
      } else if (onSuccess) {
        onSuccess();
      }

      onClose();
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || "Erro ao salvar perfil", {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({ perfil: "", permissoes: [], patentes_ids: [] });
    setSelectedPatenteIds(new Set());
    setErrors({});
    setTodasPermissoes([]);
    setTodasPatentes([]);
    setFilterText('');
    onClose();
  };

  const formatarNomePermissao = (permissao: Permissao) => {
    const categoria = permissao.categoria || "Sem Categoria";
    const nomeFormatado = permissao.permissao
      .replace(/_/g, " ")
      .replace(/\b\w/g, (l) => l.toUpperCase());
    return `${categoria} - ${nomeFormatado}`;
  };

  const adicionarPermissao = (permissao: string) => {
    setFormData(prev => ({
      ...prev,
      permissoes: [...prev.permissoes, permissao]
    }));
  };

  const removerPermissao = (permissao: string) => {
    setFormData(prev => ({
      ...prev,
      permissoes: prev.permissoes.filter(p => p !== permissao)
    }));
  };

  const togglePatente = (id: number | string) => {
    const strId = String(id);
    setSelectedPatenteIds(prev => {
      const next = new Set(prev);
      if (next.has(strId)) next.delete(strId);
      else next.add(strId);
      return next;
    });
  };

  const permissoesDisponiveis = todasPermissoes.filter(
    p => !formData.permissoes.includes(p.permissao)
  );

  const permissoesSelecionadas = todasPermissoes.filter(
    p => formData.permissoes.includes(p.permissao)
  );

  const permissoesDisponiveisFiltradas = filterText
    ? permissoesDisponiveis.filter(p =>
        p.permissao.toLowerCase().includes(filterText.toLowerCase()) ||
        (p.categoria || '').toLowerCase().includes(filterText.toLowerCase())
      )
    : permissoesDisponiveis;

  const permissoesSelecionadasFiltradas = filterText
    ? permissoesSelecionadas.filter(p =>
        p.permissao.toLowerCase().includes(filterText.toLowerCase()) ||
        (p.categoria || '').toLowerCase().includes(filterText.toLowerCase())
      )
    : permissoesSelecionadas;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <Card className="w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <UserCheck className="h-5 w-5 text-[oklch(0.45_0.15_142)]" />
              {perfilData ? "Editar Perfil" : "Novo Perfil"}
            </CardTitle>
            <CardDescription>
              {perfilData
                ? "Atualize as informações do perfil"
                : "Crie um novo perfil de usuário"}
            </CardDescription>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClose}
            className="h-6 w-6 p-0"
            disabled={loading}
          >
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="perfil">Nome do Perfil</Label>
              <InputText
                id="perfil"
                name="perfil"
                title=""
                value={formData.perfil}
                onChange={(e) => setFormData({ ...formData, perfil: e.target.value })}
                placeholder="Digite o nome do perfil"
                error={errors.perfil}
                className="mt-1"
                disabled={loading}
              />
            </div>

            {/* Gerenciamento de Permissões */}
            <div className="space-y-3">
              <Label className="flex items-center gap-2">
                <Shield className="w-4 h-4" />
                Permissões do Perfil
              </Label>

              {loadingPermissoes ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-[oklch(0.45_0.15_142)]" />
                  <span className="ml-2 text-sm text-muted-foreground">Carregando permissões...</span>
                </div>
              ) : (
                <>
                  {/* Filtro de permissão */}
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <input
                      type="text"
                      value={filterText}
                      onChange={(e) => setFilterText(e.target.value)}
                      placeholder="Filtrar permissão..."
                      className="w-full pl-9 pr-3 py-2 text-sm border border-border rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    {filterText && (
                      <button
                        type="button"
                        onClick={() => setFilterText('')}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    {/* Permissões Disponíveis */}
                    <div className="space-y-2">
                      <h4 className="text-sm font-medium text-foreground flex items-center gap-1">
                        Disponíveis ({permissoesDisponiveisFiltradas.length}{filterText ? `/${permissoesDisponiveis.length}` : ''})
                      </h4>
                      <div className="border border-border rounded-lg p-3 h-80 overflow-y-auto bg-muted/30">
                        {permissoesDisponiveisFiltradas.length === 0 ? (
                          <p className="text-xs text-muted-foreground text-center py-6">
                            {filterText ? 'Nenhum resultado encontrado' : 'Todas as permissões foram adicionadas'}
                          </p>
                        ) : (
                          <div className="space-y-2">
                            {permissoesDisponiveisFiltradas.map((permissao) => (
                              <div
                                key={permissao.id}
                                className="flex items-center justify-between p-2 bg-background border border-border rounded hover:bg-muted hover:border-[oklch(0.45_0.15_142)] cursor-pointer transition-all"
                                onClick={() => adicionarPermissao(permissao.permissao)}
                              >
                                <span className="text-xs font-medium text-foreground">
                                  {formatarNomePermissao(permissao)}
                                </span>
                                <ChevronRight className="w-3 h-3 text-muted-foreground" />
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Permissões Selecionadas */}
                    <div className="space-y-2">
                      <h4 className="text-sm font-medium text-foreground flex items-center gap-1">
                        Selecionadas ({permissoesSelecionadasFiltradas.length}{filterText ? `/${permissoesSelecionadas.length}` : ''})
                      </h4>
                      <div className="border border-green-200 dark:border-green-800 rounded-lg p-3 h-80 overflow-y-auto bg-green-50 dark:bg-green-900/20">
                        {permissoesSelecionadasFiltradas.length === 0 ? (
                          <p className="text-xs text-muted-foreground text-center py-6">
                            {filterText ? 'Nenhum resultado encontrado' : 'Nenhuma permissão selecionada'}
                          </p>
                        ) : (
                          <div className="space-y-2">
                            {permissoesSelecionadasFiltradas.map((permissao) => (
                              <div
                                key={permissao.id}
                                className="flex items-center justify-between p-2 bg-green-100 dark:bg-green-800/30 rounded border border-green-200 dark:border-green-700 hover:bg-green-200 dark:hover:bg-green-700/40 cursor-pointer transition-all"
                                onClick={() => removerPermissao(permissao.permissao)}
                              >
                                <ChevronLeft className="w-3 h-3 text-green-600 dark:text-green-400" />
                                <span className="text-xs font-medium text-green-800 dark:text-green-200">
                                  {formatarNomePermissao(permissao)}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* Resumo das Permissões */}
              {formData.permissoes.length > 0 && (
                <div className="mt-3">
                  <p className="text-xs text-muted-foreground mb-2">Permissões selecionadas:</p>
                  <div className="flex flex-wrap gap-1">
                    {permissoesSelecionadas.map((permissao) => (
                      <Badge
                        key={permissao.id}
                        className="bg-[oklch(0.45_0.15_142)]/10 text-[oklch(0.45_0.15_142)] border-[oklch(0.45_0.15_142)]/20 text-xs"
                      >
                        <Check className="w-3 h-3 mr-1" />
                        {formatarNomePermissao(permissao)}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Patentes que recebem o perfil automaticamente */}
            <div className="space-y-3">
              <Label className="flex items-center gap-2">
                <Award className="w-4 h-4" />
                Patentes que recebem este perfil automaticamente
              </Label>
              <p className="text-xs text-muted-foreground">
                Ao promover um militar para as patentes selecionadas, este perfil será atribuído automaticamente. Se nenhuma patente for selecionada, o perfil deverá ser atribuído manualmente.
              </p>
              {todasPatentes.length === 0 ? (
                <div className="flex items-center justify-center py-6 border border-border rounded-lg">
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground mr-2" />
                  <span className="text-sm text-muted-foreground">Carregando patentes...</span>
                </div>
              ) : (
                <div className="border border-border rounded-lg p-3 max-h-52 overflow-y-auto bg-muted/20">
                  <div className="grid grid-cols-2 gap-2">
                    {todasPatentes.map((patente) => {
                      const selecionada = selectedPatenteIds.has(String(patente.id));
                      return (
                        <div
                          key={patente.id}
                          onClick={() => togglePatente(patente.id)}
                          className={`flex items-center gap-2 p-2 rounded-md border cursor-pointer transition-all select-none ${
                            selecionada
                              ? "bg-[oklch(0.45_0.15_142)]/10 border-[oklch(0.45_0.15_142)]/40 text-[oklch(0.35_0.15_142)]"
                              : "bg-background border-border hover:bg-muted hover:border-[oklch(0.45_0.15_142)]/30"
                          }`}
                        >
                          <div className={`w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center ${
                            selecionada
                              ? "bg-[oklch(0.45_0.15_142)] border-[oklch(0.45_0.15_142)]"
                              : "border-border"
                          }`}>
                            {selecionada && <Check className="w-2.5 h-2.5 text-white" />}
                          </div>
                          <span className="text-xs font-medium truncate">
                            {patente.abreviacao} — {patente.patente}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              {selectedPatenteIds.size > 0 && (
                <div className="flex flex-wrap gap-1">
                  {Array.from(selectedPatenteIds).map((strId) => {
                    const pat = todasPatentes.find(p => String(p.id) === strId);
                    if (!pat) return null;
                    return (
                      <Badge
                        key={strId}
                        className="bg-[oklch(0.45_0.15_142)]/10 text-[oklch(0.45_0.15_142)] border-[oklch(0.45_0.15_142)]/20 text-xs cursor-pointer"
                        onClick={() => togglePatente(strId)}
                      >
                        <Award className="w-3 h-3 mr-1" />
                        {pat.abreviacao || pat.patente}
                        <X className="w-3 h-3 ml-1" />
                      </Badge>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="flex gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                className="flex-1"
                disabled={loading}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="flex-1 bg-[oklch(0.45_0.15_142)] hover:bg-[oklch(0.40_0.15_142)] text-white"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {perfilData ? "Atualizando..." : "Criando..."}
                  </>
                ) : (
                  perfilData ? "Atualizar" : "Criar"
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
